import { useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { Sparkles, Zap } from "lucide-react";
import { ProductGrid } from "../product";
import { SEO } from "../SEO";

const KIDS_HERO_IMAGE = "https://customer-assets.emergentagent.com/job_57f3e4d8-87fc-4053-ba36-5d4ff7f3cc21/artifacts/qnti1f22_Untitled.png";

function KidsHero({ onShopClick }) {
  return (
    <div
      className="relative overflow-hidden rounded-3xl border border-cyan-400/30 bg-gradient-to-br from-zinc-950 via-indigo-950/40 to-cyan-950/30 mb-16 shadow-[0_0_60px_-15px_rgba(34,211,238,0.45)]"
      data-testid="children-hero-banner"
    >
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 80% 30%, rgba(244,114,182,0.25), transparent 50%), radial-gradient(circle at 15% 85%, rgba(34,211,238,0.30), transparent 55%)" }} />
      <div className="absolute -top-32 -right-16 w-[28rem] h-[28rem] rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-16 w-80 h-80 rounded-full bg-pink-500/10 blur-3xl pointer-events-none" />

      <div className="relative grid md:grid-cols-2 gap-8 md:gap-12 items-center p-8 sm:p-12 md:p-16">
        <div className="space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-400/30">
            <Sparkles size={12} className="text-cyan-300 animate-pulse" />
            <span className="text-cyan-200 text-xs tracking-[0.25em] uppercase font-semibold">New Drop · Kids Anime Capsule</span>
          </div>

          <h1 className="text-white font-bebas leading-[0.95] text-5xl sm:text-6xl md:text-7xl">
            Power Level
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-pink-300 to-amber-200"> Over 9000.</span>
          </h1>

          <p className="text-white/70 text-base sm:text-lg max-w-md leading-relaxed">
            Saiyan-strong tees built for the next generation. Soft, durable cotton with bold Dragon Ball Z artwork that turns playground recess into the Cell Games.
          </p>

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              onClick={onShopClick}
              className="group px-7 py-3.5 bg-gradient-to-r from-cyan-400 to-pink-500 text-black font-bold rounded-full text-sm tracking-wide hover:from-cyan-300 hover:to-pink-400 transition-all shadow-lg shadow-cyan-500/30 hover:scale-[1.03]"
              data-testid="children-shop-now"
            >
              Shop The Kids Drop
              <span className="inline-block ml-2 transition-transform group-hover:translate-x-1">→</span>
            </button>
            <div className="flex items-center gap-3 text-xs text-white/50 pl-2">
              <span className="px-2 py-1 rounded bg-white/5 border border-white/10">5 styles</span>
              <span className="px-2 py-1 rounded bg-white/5 border border-white/10">From $27.00</span>
              <span className="px-2 py-1 rounded bg-cyan-500/10 border border-cyan-400/30 text-cyan-200">Sizes 4T–14/16</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-4">
            {["Saiyan", "Fusion", "Kamehameha", "Z-Squad"].map((word) => (
              <span key={word} className="text-[11px] tracking-widest uppercase text-cyan-200/70 px-2.5 py-1 border border-cyan-300/20 rounded-sm">
                {word}
              </span>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="relative aspect-square max-w-md mx-auto">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-cyan-400/20 via-transparent to-pink-500/20 blur-xl" />
            <img
              src={KIDS_HERO_IMAGE}
              alt="DBZ Vegito Fusion Kids Tee"
              loading="eager"
              className="relative w-full h-full object-contain drop-shadow-[0_25px_45px_rgba(34,211,238,0.35)]"
              data-testid="children-hero-image"
            />
            <div className="absolute -top-3 -right-3 sm:top-4 sm:-right-2 px-3 py-1.5 rounded-full bg-black border border-cyan-300/40 text-cyan-200 text-[11px] font-bold tracking-wider shadow-lg shadow-cyan-500/10 flex items-center gap-1">
              <Zap size={11} className="text-amber-300" /> ANIME · KIDS EDIT
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ChildrenSection({ products, isPage }) {
  const navigate = useNavigate();
  const childrenProducts = useMemo(
    () => products.filter((p) => p.category === "Children"),
    [products]
  );

  const scrollToGrid = () => {
    const el = document.getElementById("children-grid");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      className={`py-20 px-4 ${isPage ? "pt-32" : ""} bg-gradient-to-b from-black via-zinc-950 to-black`}
      data-testid="children-section"
    >
      <div className="max-w-7xl mx-auto">
        {isPage && (
          <SEO
            title="Kids Anime Collection · Dragon Ball Z Tees"
            description="Fresh Po'Boy Kids Anime Collection. Premium soft cotton kids tees featuring bold Dragon Ball Z artwork. Sizes 4T – 14/16."
            path="/children"
            image={KIDS_HERO_IMAGE}
          />
        )}

        {isPage && <KidsHero onShopClick={scrollToGrid} />}

        {!isPage && (
          <div className="text-center mb-12">
            <span className="text-cyan-400 text-sm tracking-widest uppercase">New · Kids Capsule</span>
            <h2 className="text-white text-5xl sm:text-6xl font-bebas mt-2">
              Kids <span className="text-cyan-400">Anime</span>
            </h2>
            <p className="text-white/60 mt-4 max-w-xl mx-auto">
              Saiyan-strong tees built for the next generation.
            </p>
          </div>
        )}

        <div id="children-grid">
          <ProductGrid products={childrenProducts} />
        </div>

        {!isPage && childrenProducts.length > 0 && (
          <div className="text-center mt-12">
            <button
              onClick={() => navigate("/children")}
              className="px-8 py-4 bg-cyan-400 text-black font-semibold hover:bg-cyan-300 transition-colors"
              data-testid="view-children-collection-btn"
            >
              View Kids Collection
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
